/* AudioLearn 2026 — product.js
   Format (variant) selector with full state sync per I-007:
   price, compare-at, availability, SKU, media, delivery note, inclusions,
   add-to-cart payload, and URL ?variant= parameter. Plus sticky purchase bars. */

(function () {
  'use strict';

  /* Legacy descriptions may still carry an empty responsive Issuu shell after
     the embed has been removed. Remove it at runtime as a second guard so an
     old cached stylesheet can never restore the large blank block below the
     overview copy. */
  document.querySelectorAll('.pdp-full-description__course-copy > div[style*="padding-top"]').forEach(function (orphan) {
    if (!orphan.querySelector('iframe, video, img, object, embed')) orphan.remove();
  });

  /* Legacy product descriptions can contain a generic "Why study … with
     AudioLearn?" sales pitch after the actual curriculum. It is neither
     course-specific nor useful in the overview, and should never reappear
     when an older description is edited. Remove only that heading and its
     contiguous explanatory copy; the surrounding course material remains
     untouched. */
  document.querySelectorAll('.pdp-full-description__course-copy').forEach(function (copy) {
    var nodes = Array.prototype.slice.call(copy.children);
    nodes.forEach(function (node) {
      if (!/^H[1-6]$/.test(node.tagName) || !/^why study\b.*\bwith audiolearn\?*$/i.test((node.textContent || '').trim())) return;
      var next = node.nextElementSibling;
      node.remove();
      while (next && !/^H[1-6]$/.test(next.tagName)) {
        var following = next.nextElementSibling;
        next.remove();
        next = following;
      }
    });
  });

  /* Keep legacy question-and-answer copy out of the Overview.  Those prompts
     are navigation content, not course narrative: the dedicated FAQ and
     practice-material sections are the single source of truth.  Merchant
     descriptions were authored over many years, so remove complete sentences
     containing the concept rather than relying on one product-specific phrase.
     This deliberately leaves ordinary references to practice exams intact. */
  document.querySelectorAll('.pdp-full-description__course-copy').forEach(function (copy) {
    if (copy.dataset.pdpOverviewQaSanitized === 'true') return;
    copy.dataset.pdpOverviewQaSanitized = 'true';
    var qaPattern = /\b(?:q\s*(?:&|and)\s*a|questions?\s*(?:&|and)\s*answers?|question[-\s]*and[-\s]*answer(?:s)?)\b/i;
    Array.prototype.slice.call(copy.querySelectorAll('p, li')).forEach(function (block) {
      var text = (block.textContent || '').replace(/\s+/g, ' ').trim();
      if (!qaPattern.test(text)) return;
      var sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
      var retained = sentences.filter(function (sentence) {
        return !qaPattern.test(sentence);
      }).join(' ').replace(/\s+/g, ' ').trim();
      if (!retained) {
        block.remove();
        return;
      }
      /* These legacy blocks use plain text; replacing only the affected block
         avoids altering neighboring curriculum markup or headings. */
      block.textContent = retained;
    });
  });

  function sameMediaSource(currentSource, nextSource) {
    if (!currentSource || !nextSource) return false;
    try {
      return new URL(currentSource, window.location.origin).pathname === new URL(nextSource, window.location.origin).pathname;
    } catch (error) {
      return currentSource === nextSource;
    }
  }

  window.customElements.define('al-product-form', class extends HTMLElement {
    connectedCallback() {
      var json = this.querySelector('[data-variants-json]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.tiles = Array.prototype.slice.call(this.querySelectorAll('.format-tile'));
      this.idInput = this.querySelector('input[name="id"]');
      this.tiles.forEach(function (tile) {
        tile.addEventListener('click', function () {
          if (tile.disabled) return;
          this.select(Number(tile.dataset.variantId));
        }.bind(this));
      }, this);

      var params = new URLSearchParams(window.location.search);
      var fromUrl = Number(params.get('variant'));
      var initial = this.variants.filter(function (v) { return v.id === fromUrl; })[0];
      /* Liquid already renders the selected variant's full anniversary card.
         Preserve that server-rendered card on ordinary page loads: rebuilding
         it in JavaScript was the source of the compact/unboxed regression.
         A URL-selected variant still needs to hydrate its price. */
      this.hasVariantInUrl = Boolean(initial);
      var current = initial || this.variants.filter(function (v) { return String(v.id) === this.idInput.value; }, this)[0];
      if (current) this.select(current.id, true);
    }

    formatMoney(cents) {
      /* Preserve the selected variant's exact amount. Shopify supplies prices
         in minor units, so non-zero cents must never be truncated before the
         cart or checkout reveals them. */
      var amount = (Number(cents || 0) / 100).toFixed(2);
      var parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      amount = parts.join('.');
      var token = /\{\{\s*amount(?:_[a-z_]+)?\s*\}\}/;
      return token.test(this.moneyFormat) ? this.moneyFormat.replace(token, amount) : '$' + amount;
    }

    select(variantId, initial) {
      var variant = this.variants.filter(function (v) { return v.id === variantId; })[0];
      if (!variant) return;
      var root = document;

      /* tiles */
      this.tiles.forEach(function (tile) {
        tile.setAttribute('aria-pressed', String(Number(tile.dataset.variantId) === variantId));
      });

      /* hidden input → add-to-cart payload */
      this.idInput.value = variant.id;
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));

      /* price */
      root.querySelectorAll('[data-product-price]').forEach(function (el) {
        el.textContent = this.formatMoney(variant.price);
      }, this);
      root.querySelectorAll('[data-sticky-price]').forEach(function (el) {
        el.textContent = this.formatMoney(variant.price);
      }, this);
      if (!initial || this.hasVariantInUrl) root.querySelectorAll('[data-product-price-anniversary]').forEach(function (el) {
        var salePrice = this.formatMoney(variant.price);
        var hasAnniversaryDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
        /* The hero has a stable structural class as a fallback so a missing
           data attribute can never silently downgrade it to compact markup. */
        var presentation = el.dataset.pricePresentation || (el.classList.contains('pdp-price-row') ? 'hero' : 'compact');
        var anniversaryMarkup = '<span class="anniversary-price anniversary-price--' + presentation + '">';
        if (hasAnniversaryDiscount) {
          var listPrice = this.formatMoney(variant.compare_at_price);
          var savings = this.formatMoney(variant.compare_at_price - variant.price);
          if (presentation === 'hero') {
            anniversaryMarkup += '<span class="anniversary-price__label">30th Anniversary Special</span><span class="anniversary-price__hero-content"><span class="anniversary-price__figures"><span class="anniversary-price__sale">' + salePrice + '</span><span class="anniversary-price__comparison"><span class="anniversary-price__list"><s>' + listPrice + '</s></span><span class="anniversary-price__savings">SAVE ' + savings + '</span></span></span><span class="anniversary-price__promo-copy"><span class="anniversary-price__message">Celebrating three decades of learning since 1997</span><span class="anniversary-price__note">The same trusted AudioLearn quality. A special anniversary offer for today’s learners.</span></span></span>';
          } else {
            anniversaryMarkup += '<span class="anniversary-price__sale">' + salePrice + '</span><span class="anniversary-price__comparison"><span class="anniversary-price__list"><s>' + listPrice + '</s></span><span class="anniversary-price__savings">SAVE ' + savings + '</span></span><span class="anniversary-price__label">30th Anniversary Special</span><span class="anniversary-price__message">Celebrating three decades of learning since 1997</span>';
          }
        } else {
          anniversaryMarkup += '<span class="anniversary-price__sale">' + salePrice + '</span>';
        }
        el.innerHTML = anniversaryMarkup + '</span>';
      }, this);
      root.querySelectorAll('[data-product-compare]').forEach(function (el) {
        var has = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.hidden = !has;
        if (has) el.textContent = this.formatMoney(variant.compare_at_price);
      }, this);
      root.querySelectorAll('[data-product-save]').forEach(function (el) {
        var has = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.hidden = !has;
        if (has) el.textContent = 'Save ' + this.formatMoney(variant.compare_at_price - variant.price);
      }, this);

      /* availability + add-to-cart */
      root.querySelectorAll('[data-availability]').forEach(function (el) {
        el.querySelectorAll('[data-state]').forEach(function (s) {
          s.hidden = s.dataset.state !== (variant.available ? 'in' : 'out');
        });
      });
      root.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
        btn.disabled = !variant.available;
      });

      /* SKU */
      root.querySelectorAll('[data-product-sku]').forEach(function (el) {
        el.textContent = variant.sku || '';
        el.closest('[data-sku-row]') && (el.closest('[data-sku-row]').hidden = !variant.sku);
      });

      root.querySelectorAll('[data-format-card-id]').forEach(function (card) {
        card.classList.toggle('is-selected', Number(card.dataset.formatCardId) === variantId);
      });

      /* variant image */
      if (variant.featured_media_url) {
        root.querySelectorAll('[data-product-main-image]').forEach(function (img) {
          /* The server-rendered primary cover already matches the initially
             selected variant. Replacing it here discards its responsive srcset
             and creates a duplicate request before the visitor changes format. */
          if (!initial || !sameMediaSource(img.currentSrc || img.src, variant.featured_media_url)) {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = variant.featured_media_url;
          }
          img.alt = variant.featured_media_alt || img.dataset.defaultAlt || '';
        });
        root.querySelectorAll('[data-media-thumb]').forEach(function (thumb) {
          thumb.setAttribute('aria-current', String(thumb.dataset.mediaUrl === variant.featured_media_url));
        });
      }

      /* per-format metafield panels: delivery note + includes (pre-rendered) */
      root.querySelectorAll('[data-variant-panel]').forEach(function (panel) {
        panel.hidden = Number(panel.dataset.variantPanel) !== variantId;
      });

      /* URL ?variant= */
      if (!initial && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  });

  /* Product media rail */
  document.querySelectorAll('[data-media-thumb]').forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      var image = document.querySelector('[data-product-main-image]');
      if (!image || !thumb.dataset.mediaUrl) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = thumb.dataset.mediaUrl;
      image.alt = thumb.dataset.mediaAlt || image.dataset.defaultAlt || '';
      document.querySelectorAll('[data-media-thumb]').forEach(function (candidate) {
        candidate.setAttribute('aria-current', String(candidate === thumb));
      });
    });
  });

  /* The product page is continuous. Both section menus stay in sync with the
     section currently in view instead of hiding product information in tabs. */
  var scrollSections = Array.prototype.slice.call(document.querySelectorAll('[data-pdp-scroll-section]'));
  var scrollLinks = Array.prototype.slice.call(document.querySelectorAll('[data-pdp-scroll-link]'));
  var selectedProductSection = '';
  var selectedProductSectionTimer = null;
  function setActiveProductSection(key) {
    document.querySelectorAll('[data-pdp-tab]').forEach(function (tab) {
      var activeTab = tab.dataset.pdpTab === key;
      tab.setAttribute('aria-selected', String(activeTab));
      tab.tabIndex = activeTab ? 0 : -1;
    });
    scrollLinks.forEach(function (link) {
      var active = link.dataset.pdpScrollLink === key;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }
  if (scrollSections.length && scrollLinks.length) {
    scrollLinks.forEach(function (link) {
      link.addEventListener('click', function () {
        selectedProductSection = link.dataset.pdpScrollLink;
        if (selectedProductSectionTimer) window.clearTimeout(selectedProductSectionTimer);
        selectedProductSectionTimer = window.setTimeout(function () {
          selectedProductSection = '';
          scheduleProductSectionUpdate();
        }, 1000);
        setActiveProductSection(selectedProductSection);
      });
    });
    /* Track against the page position rather than observer callback entries.
       An IntersectionObserver callback only contains elements whose state just
       changed, so a previous Overview callback could overwrite the selected
       Included/Sample/FAQ button immediately after a smooth-scroll click. */
    var productSectionTicking = false;
    var normalizedProductSection = function (key) {
      return (key || '').split('-')[0];
    };
    var updateActiveProductSectionFromScroll = function () {
      productSectionTicking = false;
      /* A touch tap begins a smooth scroll before the requested section reaches
         the activation line. Preserve the visitor's explicit selection until
         that motion settles, so mobile navigation cannot snap back to
         “Overview” midway through the move. */
      if (selectedProductSection) return;
      var activationLine = 180;
      var activeKey = normalizedProductSection(scrollSections[0].dataset.pdpScrollSection);
      scrollSections.forEach(function (section) {
        if (section.getBoundingClientRect().top <= activationLine) {
          activeKey = normalizedProductSection(section.dataset.pdpScrollSection);
        }
      });
      setActiveProductSection(activeKey);
    };
    var scheduleProductSectionUpdate = function () {
      if (productSectionTicking) return;
      productSectionTicking = true;
      window.requestAnimationFrame(updateActiveProductSectionFromScroll);
    };
    window.addEventListener('scroll', scheduleProductSectionUpdate, { passive: true });
    window.addEventListener('resize', scheduleProductSectionUpdate);
    scheduleProductSectionUpdate();
  }

  /* Product section navigation is intentionally a continuous page, not a
     progressively hidden tab widget.  Keep every Overview/Included/Sample/FAQ
     control wired to the same section, including the compact sticky bar.  The
     old implementation relied on the generic <al-tabs> element even though
     product navigation is rendered as plain buttons; after a theme update the
     buttons therefore stopped responding intermittently. */
  (function initProductSectionNavigation() {
    var buttons = Array.prototype.slice.call(document.querySelectorAll('[data-pdp-tab]'));
    if (!buttons.length) return;
    /* Product details are a continuous page. Older product markup may retain
       a hidden attribute from the retired tab-panel implementation; clear it
       before binding controls so every section remains reachable on mobile. */
    document.querySelectorAll('[data-pdp-panel]').forEach(function (panel) {
      panel.hidden = false;
    });
    var sectionFor = function (key) {
      return document.querySelector('[data-pdp-scroll-section="' + key + '"]') ||
        document.querySelector('[data-pdp-panel="' + key + '"]');
    };
    buttons.forEach(function (button) {
      if (button.dataset.pdpNavigationBound === 'true') return;
      button.dataset.pdpNavigationBound = 'true';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var section = sectionFor(button.dataset.pdpTab);
        if (!section) return;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        selectedProductSection = button.dataset.pdpTab;
        if (selectedProductSectionTimer) window.clearTimeout(selectedProductSectionTimer);
        selectedProductSectionTimer = window.setTimeout(function () {
          selectedProductSection = '';
          scheduleProductSectionUpdate();
        }, reduce ? 0 : 1000);
        section.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        setActiveProductSection(button.dataset.pdpTab);
      });
    });
  }());

  /* Course descriptions occasionally contain a merchant-authored FAQ block.
     It belongs in the dedicated FAQ section for every title—not inside the
     overview narrative. Move only an explicitly labelled Q&A block, preserving
     its original content and leaving later non-FAQ headings in Overview. */
  document.querySelectorAll('.pdp-full-description__course-copy').forEach(function (overview) {
    var productPage = overview.closest('.template-product') || document;
    var faqPanel = productPage.querySelector('.pdp-faq-panel');
    if (!faqPanel) return;
    var faqHeadingPattern = /^(?:frequently asked questions|faq(?:s)?|questions and answers|common questions|course questions|questions about (?:this )?(?:course|title|product))$/i;
    var elements = Array.prototype.slice.call(overview.children);
    var faqStartIndex = elements.findIndex(function (element) {
      return faqHeadingPattern.test((element.textContent || '').replace(/\s+/g, ' ').trim());
    });
    if (faqStartIndex < 0) return;

    var faqStart = elements[faqStartIndex];
    if (!/^H[1-6]$/.test(faqStart.tagName)) {
      var faqLabel = document.createElement('h3');
      faqLabel.textContent = faqStart.textContent;
      faqStart.parentNode.replaceChild(faqLabel, faqStart);
      faqStart = faqLabel;
      elements[faqStartIndex] = faqStart;
    }

    var block = document.createElement('section');
    block.className = 'pdp-migrated-faqs';
    block.setAttribute('aria-label', 'Additional course questions');
    var isQuestionHeading = function (element) {
      var text = (element.textContent || '').replace(/\s+/g, ' ').trim();
      return /\?$/.test(text) || /^(?:q(?:uestion)?\s*\d*\s*[:.-]|what\b|why\b|when\b|where\b|who\b|which\b|how\b|can\b|do\b|does\b|is\b|are\b|will\b)/i.test(text);
    };
    var node = faqStart;
    while (node) {
      var next = node.nextElementSibling;
      /* A later heading marks the beginning of a new overview section, so do
         not accidentally pull unrelated product details into FAQs. Individual
         question headings stay with the FAQ block. */
      if (node !== faqStart && /^H[1-6]$/.test(node.tagName) && !isQuestionHeading(node)) break;
      block.appendChild(node);
      node = next;
    }
    if (block.childElementCount) faqPanel.appendChild(block);
  });

  /* Some older descriptions do not have an "FAQ" label at all: they append
     individual question headings directly after the curriculum. Treat those
     headings as FAQs too. This moves each question and its answer into the
     existing accordion, keeping the overview strictly course narrative while
     preserving the merchant-authored answer in a discoverable place. */
  document.querySelectorAll('.pdp-full-description__course-copy').forEach(function (overview) {
    if (overview.dataset.pdpQuestionHeadingsMigrated === 'true') return;
    overview.dataset.pdpQuestionHeadingsMigrated = 'true';

    var productPage = overview.closest('.template-product') || document;
    var faqPanel = productPage.querySelector('.pdp-faq-panel');
    var accordion = faqPanel && faqPanel.querySelector('.accordion');
    if (!accordion) return;

    Array.prototype.slice.call(overview.children).forEach(function (heading) {
      if (!/^H[1-6]$/.test(heading.tagName)) return;
      var question = (heading.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/\?$/.test(question)) return;

      var content = document.createElement('div');
      content.className = 'accordion__content';
      var next = heading.nextElementSibling;
      while (next && !/^H[1-6]$/.test(next.tagName)) {
        var following = next.nextElementSibling;
        content.appendChild(next);
        next = following;
      }

      var details = document.createElement('details');
      var summary = document.createElement('summary');
      var label = document.createElement('span');
      label.textContent = question;
      summary.appendChild(label);
      details.appendChild(summary);
      if (content.childElementCount) details.appendChild(content);
      heading.remove();
      accordion.appendChild(details);
    });
  });

  /* Replace the dated college-course boilerplate anywhere it appears with a
     sharper, course-specific introduction without touching the topic list or
     merchant-authored details that follow it. */
  document.querySelectorAll('[data-pdp-college-course-copy]').forEach(function (copy) {
    var paragraphs = Array.prototype.slice.call(copy.querySelectorAll(':scope > p'));
    var openingIndex = paragraphs.findIndex(function (paragraph) {
      return /college-level courses? presents?/i.test(paragraph.textContent || '');
    });
    if (openingIndex < 0) return;
    var opening = paragraphs[openingIndex];
    var followup = paragraphs[openingIndex + 1];
    var courseTitle = (document.querySelector('.pdp-summary h1') || document.querySelector('h1') || {}).textContent || 'this course';
    courseTitle = courseTitle.trim();
    var polishedOpening = document.createElement('p');
    polishedOpening.className = 'pdp-college-course-copy__opening';
    var emphasis = document.createElement('strong');
    emphasis.textContent = 'Build a stronger foundation in ' + courseTitle + ', on your schedule.';
    polishedOpening.appendChild(emphasis);
    polishedOpening.appendChild(document.createTextNode(' AudioLearn turns college-level concepts into clear, professionally narrated lessons you can revisit wherever your day takes you.'));
    var polishedFollowup = document.createElement('p');
    polishedFollowup.textContent = 'Stay focused on the material that matters most. Bite-sized chapters move from core principles to practical applications, with key takeaways and quizzes that reinforce your knowledge and help you check your progress.';
    opening.replaceWith(polishedOpening);
    if (followup && /focused and high-yield|key takeaways|commonly tested/i.test(followup.textContent || '')) {
      followup.replaceWith(polishedFollowup);
    } else {
      polishedOpening.insertAdjacentElement('afterend', polishedFollowup);
    }
  });

  /* Gallery "Look inside" opens the PDF sampler as a modal. */
  document.querySelectorAll('[data-pdp-open-tab]').forEach(function (trigger) {
    trigger.addEventListener('click', function (event) {
      if (event) event.preventDefault();
      var previewTarget = trigger.dataset.pdpScrollTarget && document.getElementById(trigger.dataset.pdpScrollTarget);
      if (previewTarget && previewTarget.matches('[data-pdp-ebook-preview]')) {
        if (!previewTarget.open) {
          if (typeof previewTarget.showModal === 'function') previewTarget.showModal();
          else previewTarget.setAttribute('open', '');
        }
        previewTarget.dataset.pdpEbookActivated = 'true';
        previewTarget.dispatchEvent(new CustomEvent('pdp-ebook-activate'));
        var closeButton = previewTarget.querySelector('[data-pdp-ebook-close]');
        if (closeButton) window.setTimeout(function () { closeButton.focus(); }, 0);
        return;
      }
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var scrollBehavior = reduceMotion ? 'auto' : 'smooth';
      var target = trigger.dataset.pdpScrollTarget && document.getElementById(trigger.dataset.pdpScrollTarget);
      if (target) {
        target.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
        if (trigger.dataset.pdpOpenTab === 'reviews') {
          var heading = target.querySelector('h2[tabindex="-1"]');
          if (heading) {
            window.setTimeout(function () { heading.focus({ preventScroll: true }); }, reduceMotion ? 0 : 350);
          }
        }
        return;
      }
      var panel = document.querySelector('[data-pdp-scroll-section="' + trigger.dataset.pdpOpenTab + '"]');
      if (panel) panel.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    });
  });

  document.querySelectorAll('[data-pdp-ebook-preview]').forEach(function (preview) {
    var closeButton = preview.querySelector('[data-pdp-ebook-close]');
    var closePreview = function () {
      if (typeof preview.close === 'function') preview.close();
      else preview.removeAttribute('open');
    };
    if (closeButton) closeButton.addEventListener('click', closePreview);
    preview.addEventListener('click', function (event) {
      if (event.target === preview) closePreview();
    });
  });

  /* Native selected-page eBook preview. The manifest points to page images
     extracted from each course PDF; no third-party reader is embedded. */
  document.querySelectorAll('[data-pdf-page-manifest-url][data-product-handle]').forEach(function (productForm) {
    var manifestUrl = productForm.dataset.pdfPageManifestUrl;
    var handle = productForm.dataset.productHandle;
    if (!manifestUrl || !handle) return;
    fetch(manifestUrl)
      .then(function (response) { return response.ok ? response.json() : Promise.reject(); })
      .then(function (manifest) {
        var entry = manifest[handle];
        if (!entry || !entry.page_count) return;
        document.querySelectorAll('[data-pdp-ebook-page-count]').forEach(function (count) {
          count.textContent = entry.page_count + ' pages';
          count.hidden = false;
        });
        document.querySelectorAll('[data-pdp-ebook-page-count-fallback]').forEach(function (fallback) {
          fallback.hidden = true;
        });
        productForm.querySelectorAll('[data-pdp-follow-along-page-count]').forEach(function (count) {
          count.textContent = entry.page_count + ' pages of follow along PDF manual';
          count.hidden = false;
        });
      })
      .catch(function () { /* The metafield fallback remains visible. */ });
  });

  function hasVisibleChapterCount() {
    return Array.prototype.some.call(
      document.querySelectorAll('[data-pdp-chapters-metric], [data-pdp-chapters-check], [data-pdp-chapters-detail], [data-pdp-chapters-overview]'),
      function (element) { return !element.hidden; }
    );
  }

  function hydrateChapterCount(count) {
    if (!count || hasVisibleChapterCount()) return;
    var chapterLabel = count + ' chapters';
    document.querySelectorAll('[data-pdp-chapters]').forEach(function (element) {
      element.textContent = chapterLabel;
    });
    document.querySelectorAll('[data-pdp-chapters-metric], [data-pdp-chapters-check], [data-pdp-chapters-detail], [data-pdp-chapters-overview]').forEach(function (element) {
      element.hidden = false;
    });
  }

  /* Authoritative product metafields take precedence. For catalog records that
     have not yet been migrated, hydrate runtime and chapter totals from the
     verified catalog backup rather than leaving the detail fields blank. */
  document.querySelectorAll('[data-course-details-manifest-url][data-product-handle]').forEach(function (productForm) {
    var manifestUrl = productForm.dataset.courseDetailsManifestUrl;
    var handle = productForm.dataset.productHandle;
    if (!manifestUrl || !handle) return;
    fetch(manifestUrl)
      .then(function (response) { return response.ok ? response.json() : Promise.reject(); })
      .then(function (manifest) {
        var entry = manifest[handle];
        if (!entry) return;
        var runtimeFact = document.querySelector('[data-pdp-runtime-fact]');
        var hasRuntime = runtimeFact && !runtimeFact.hidden;

        if (!hasRuntime && entry.runtime) {
          document.querySelectorAll('[data-pdp-runtime]').forEach(function (element) {
            element.textContent = entry.runtime;
          });
          document.querySelectorAll('[data-pdp-runtime-fact], [data-pdp-runtime-metric], [data-pdp-runtime-check], [data-pdp-runtime-detail], [data-pdp-runtime-overview]').forEach(function (element) {
            element.hidden = false;
          });
          document.querySelectorAll('[data-pdp-runtime-format]').forEach(function (element) {
            element.textContent = entry.runtime + ' of course audio';
          });
        }

        var manifestChapterCount = entry.chapters || entry.audio_file_count || entry.toc_chapter_count;
        if (Array.isArray(entry.audio_files)) manifestChapterCount = entry.audio_files.length;
        if (Array.isArray(entry.toc_chapters)) manifestChapterCount = entry.toc_chapters.length;
        hydrateChapterCount(manifestChapterCount);
      })
      .catch(function () { /* Product metafields and neutral fallbacks remain visible. */ });
  });

  /* Counts audited from the source audio files are kept separate from the
     broader course-details catalog so a later catalog import cannot erase
     verified chapter totals. */
  document.querySelectorAll('[data-course-chapter-counts-url][data-product-handle]').forEach(function (productForm) {
    var chapterCountsUrl = productForm.dataset.courseChapterCountsUrl;
    var handle = productForm.dataset.productHandle;
    if (!chapterCountsUrl || !handle || hasVisibleChapterCount()) return;
    fetch(chapterCountsUrl)
      .then(function (response) { return response.ok ? response.json() : Promise.reject(); })
      .then(function (chapterCounts) { hydrateChapterCount(chapterCounts[handle]); })
      .catch(function () { /* Do not invent a total when a source count is unavailable. */ });
  });

  document.querySelectorAll('[data-pdp-ebook-preview]').forEach(function (preview) {
    var manifestUrl = preview.dataset.previewManifestUrl;
    var handle = preview.dataset.productHandle;
    var image = preview.querySelector('[data-pdp-ebook-image]');
    var caption = preview.querySelector('[data-pdp-ebook-caption]');
    var controls = preview.querySelector('[data-pdp-ebook-controls]');
    var stage = preview.querySelector('[data-pdp-ebook-stage]');
    var previous = preview.querySelector('[data-pdp-ebook-previous]');
    var next = preview.querySelector('[data-pdp-ebook-next]');
    var turnTimer;
    if (!manifestUrl || !handle || !image || !caption || !controls || !stage) return;

    fetch(manifestUrl)
      .then(function (response) { return response.ok ? response.json() : Promise.reject(); })
      .then(function (manifest) {
        var entry = manifest[handle];
        if (!entry || !entry.asset_id) {
          preview.hidden = true;
          return;
        }
        var pages = [{ key: 'cover', page: entry.pages && entry.pages.cover, label: 'Cover', caption: 'Course cover' }]
          .concat((entry.toc_pages || (entry.pages && entry.pages.toc ? [entry.pages.toc] : [])).map(function (page, index) {
            return { key: 'toc-' + index, page: page, label: 'Contents ' + (index + 1), caption: 'Table of contents — page ' + (index + 1) };
          }))
          .concat((entry.sample_pages || (entry.pages && entry.pages.sample ? [entry.pages.sample] : [])).map(function (page, index) {
            return { key: 'sample-' + index, page: page, label: 'Sample ' + (index + 1), caption: 'Selected course sample — page ' + (index + 1) };
          }))
          .filter(function (item) { return item.page; });
        if (!pages.length) {
          preview.hidden = true;
          return;
        }
        document.querySelectorAll('[data-pdp-ebook-page-count]').forEach(function (count) {
          if (!entry.page_count) return;
          count.textContent = entry.page_count + ' pages';
          count.hidden = false;
        });
        if (entry.page_count) {
          document.querySelectorAll('[data-pdp-ebook-page-count-fallback]').forEach(function (fallback) {
            fallback.hidden = true;
          });
          document.querySelectorAll('[data-pdp-follow-along-page-count]').forEach(function (count) {
            count.textContent = entry.page_count + ' pages of follow along PDF manual';
            count.hidden = false;
          });
        }
        controls.innerHTML = '';
        var selectedIndex = 0;
        pages.forEach(function (item, index) {
          var button = document.createElement('button');
          button.type = 'button';
          button.dataset.pdpEbookPage = item.key;
          button.setAttribute('aria-pressed', String(index === 0));
          button.textContent = item.label;
          button.addEventListener('click', function () { selectPage(item, index); });
          controls.appendChild(button);
        });
        function updateArrows() {
          if (previous) previous.disabled = selectedIndex === 0;
          if (next) next.disabled = selectedIndex === pages.length - 1;
        }
        function selectPage(item, index) {
          var page = item.page;
          if (!page) return;
          var direction = index >= selectedIndex ? 'next' : 'previous';
          var currentImageSource = image.currentSrc || image.src;
          var existingLeaf = stage.querySelector('.pdp-ebook-sampler__turn-leaf');
          if (existingLeaf) existingLeaf.remove();
          if (turnTimer) window.clearTimeout(turnTimer);
          selectedIndex = index;
          stage.classList.remove('is-turning', 'is-turning-next', 'is-turning-previous');
          if (currentImageSource && index !== 0) {
            var leaf = document.createElement('img');
            leaf.className = 'pdp-ebook-sampler__turn-leaf';
            leaf.src = currentImageSource;
            leaf.alt = '';
            leaf.setAttribute('aria-hidden', 'true');
            leaf.style.width = image.getBoundingClientRect().width + 'px';
            leaf.style.height = image.getBoundingClientRect().height + 'px';
            stage.appendChild(leaf);
          }
          void stage.offsetWidth;
          stage.classList.add('is-turning', direction === 'next' ? 'is-turning-next' : 'is-turning-previous');
          var isCover = item.key === 'cover';
          image.src = isCover && preview.dataset.pdpCoverImage
            ? preview.dataset.pdpCoverImage
            : 'https://image.isu.pub/' + entry.asset_id + '/jpg/page_' + page + '.jpg';
          image.alt = isCover && preview.dataset.pdpCoverAlt
            ? preview.dataset.pdpCoverAlt
            : (entry.title || 'Course') + ' - ' + item.caption;
          caption.textContent = item.caption;
          preview.querySelectorAll('[data-pdp-ebook-page]').forEach(function (candidate) {
            candidate.setAttribute('aria-pressed', String(candidate.dataset.pdpEbookPage === item.key));
          });
          updateArrows();
          turnTimer = window.setTimeout(function () {
            var staleLeaf = stage.querySelector('.pdp-ebook-sampler__turn-leaf');
            if (staleLeaf) staleLeaf.remove();
            stage.classList.remove('is-turning', 'is-turning-next', 'is-turning-previous');
          }, 680);
        }
        if (previous) previous.addEventListener('click', function () {
          if (selectedIndex > 0) selectPage(pages[selectedIndex - 1], selectedIndex - 1);
        });
        if (next) next.addEventListener('click', function () {
          if (selectedIndex < pages.length - 1) selectPage(pages[selectedIndex + 1], selectedIndex + 1);
        });
        function activatePreview() {
          if (preview.dataset.pdpEbookInitialized === 'true') return;
          preview.dataset.pdpEbookInitialized = 'true';
          selectPage(pages[0], 0);
        }
        preview.addEventListener('pdp-ebook-activate', activatePreview);
        if (preview.dataset.pdpEbookActivated === 'true' || !preview.closest('[hidden]')) {
          activatePreview();
        }
      })
      .catch(function () {
        preview.hidden = true;
      });
  });

  /* Keep mobile purchase controls in the sticky header from first paint.
     Moving the existing controls preserves variant updates and form handlers. */
  var controls = document.getElementById('purchase-controls');
  var stickyDesktop = document.querySelector('.sticky-bar-desktop');
  var stickyMobile = document.querySelector('.sticky-bar-mobile');
  var headerSection = document.querySelector('.al-sticky-header-section');
  if (stickyMobile && headerSection) {
    headerSection.appendChild(stickyMobile);
    stickyMobile.classList.add('is-visible');
  }
  if (controls && stickyDesktop && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var visible = entries[0].boundingClientRect.bottom < 0;
      if (stickyDesktop) stickyDesktop.classList.toggle('is-visible', visible);
    }, { threshold: 0 }).observe(controls);
  }

})();
